/*  Phase-3 C2/C3/C4 — Debtor outstanding, receipts, and interest cost.
    Single-page overhaul with three inner tabs so it stays under one nav item:
      * Outstanding — bills + ageing bucket rollup + KYC editor per debtor.
      * Receipts    — every payment ever received, editable, tied to bills.
      * Interest    — per debtor AGREED vs ACTUAL, breaches, ₹ interest cost.
    Uses the /api/phase3/debtor_* endpoints and the extended DebtorIn KYC
    fields (bank/GST/vehicles/agreed_interest_pct).
*/
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtDateDMY, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast, Toaster } from "sonner";
import { Plus, Receipt, TrendingUp, Users, X } from "lucide-react";

export default function DebtorsPro() {
  return (
    <div className="space-y-4" data-testid="debtors-pro-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Debtors — Outstanding, Receipts & Interest</h1>
        <p className="text-sm text-slate-600 mt-1">
          Manager-maintained per-bill outstanding, receipts logged the day they arrive, and interest cost per debtor computed against Settings &raquo; Cost of funds.
        </p>
      </div>
      <Tabs defaultValue="outstanding">
        <TabsList>
          <TabsTrigger value="outstanding" data-testid="debtors-tab-outstanding"><Users size={12} className="mr-1" />Outstanding</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="debtors-tab-receipts"><Receipt size={12} className="mr-1" />Receipts</TabsTrigger>
          <TabsTrigger value="interest" data-testid="debtors-tab-interest"><TrendingUp size={12} className="mr-1" />Interest & margin</TabsTrigger>
        </TabsList>
        <TabsContent value="outstanding"><OutstandingTab /></TabsContent>
        <TabsContent value="receipts"><ReceiptsTab /></TabsContent>
        <TabsContent value="interest"><InterestTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Outstanding + Bills + Ageing ------------------------------------------
function OutstandingTab() {
  const [debtors, setDebtors] = useState([]);
  const [bills, setBills] = useState([]);
  const [ageing, setAgeing] = useState({ labels: [], rows: [] });
  const [selDebtor, setSelDebtor] = useState("");
  const [newBill, setNewBill] = useState({ debtor_id: "", bill_date: todayISO(), bill_no: "", amount: 0, tds: 0, fuel: "HSD", volume: 0, notes: "" });

  const load = async () => {
    try {
      const [d, b, a] = await Promise.all([
        api.get("/masters/debtors"),
        api.get("/phase3/debtor_bills", { params: { debtor_id: selDebtor || undefined } }),
        api.get("/phase3/debtor_ageing"),
      ]);
      setDebtors(d.data);
      setBills(b.data);
      setAgeing(a.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [selDebtor]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBill = async () => {
    if (!newBill.debtor_id || !newBill.bill_no || !newBill.amount) { toast.error("Debtor, bill no, and amount are required."); return; }
    try { await api.post("/phase3/debtor_bills", newBill); toast.success("Bill recorded."); setNewBill({ ...newBill, bill_no: "", amount: 0, tds: 0, volume: 0, notes: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Ageing buckets (all debtors)</div>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm" data-testid="ageing-table">
            <thead><tr><th className="text-left">Debtor</th>{ageing.labels.map((l) => <th key={l} className="text-right">{l}</th>)}<th className="text-right">Total ₹</th></tr></thead>
            <tbody>
              {ageing.rows.map((r) => (
                <tr key={r.debtor_id}>
                  <td>{r.debtor_name}</td>
                  {r.buckets.map((b, i) => <td key={ageing.labels[i] || `bucket-${i}`} className={"num " + (i === r.buckets.length - 1 && b > 0 ? "text-red-700 font-bold" : "")}>{fmtINR(b)}</td>)}
                  <td className="num font-bold">{fmtINR(r.total)}</td>
                </tr>
              ))}
              {ageing.rows.length === 0 && <tr><td colSpan={ageing.labels.length + 2} className="text-slate-500 italic p-3 text-center">Nothing outstanding — record bills to see ageing.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Bills</div>
          <select value={selDebtor} onChange={(e) => setSelDebtor(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm" data-testid="bills-debtor-filter">
            <option value="">All debtors</option>
            {debtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-2 border border-dashed border-slate-400 rounded-sm bg-slate-50">
          <select value={newBill.debtor_id} onChange={(e) => setNewBill({ ...newBill, debtor_id: e.target.value })} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm" data-testid="new-bill-debtor">
            <option value="">— Debtor —</option>
            {debtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
          </select>
          <Input type="date" value={newBill.bill_date} onChange={(e) => setNewBill({ ...newBill, bill_date: e.target.value })} data-testid="new-bill-date" />
          <Input placeholder="Bill no" value={newBill.bill_no} onChange={(e) => setNewBill({ ...newBill, bill_no: e.target.value })} data-testid="new-bill-no" />
          <Input type="number" step="0.01" placeholder="Amount ₹" value={newBill.amount || ""} onChange={(e) => setNewBill({ ...newBill, amount: Number(e.target.value) })} data-testid="new-bill-amount" />
          <Input type="number" step="0.01" placeholder="TDS ₹ (optional)" value={newBill.tds || ""} onChange={(e) => setNewBill({ ...newBill, tds: Number(e.target.value) })} data-testid="new-bill-tds" />
          <select value={newBill.fuel} onChange={(e) => setNewBill({ ...newBill, fuel: e.target.value })} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm">
            <option>HSD</option><option>MS</option><option>CNG</option><option>Other</option>
          </select>
          <Input type="number" step="0.01" placeholder="Volume (L/kg)" value={newBill.volume || ""} onChange={(e) => setNewBill({ ...newBill, volume: Number(e.target.value) })} data-testid="new-bill-volume" />
          <Button onClick={addBill} className="bg-slate-900 text-white h-9 text-xs" data-testid="new-bill-save"><Plus size={12} className="mr-1" />Record bill</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm" data-testid="bills-table">
            <thead><tr><th>Date</th><th>Debtor</th><th>Bill #</th><th>Amt ₹</th><th>TDS ₹</th><th>Cleared ₹</th><th>Outstanding ₹</th><th>Status</th></tr></thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.bill_id} className={b.computed_status === "OPEN" ? "state-danger" : b.computed_status === "PARTIAL" ? "state-warn" : ""} data-testid={`bill-row-${b.bill_id}`}>
                  <td>{fmtDateDMY(b.bill_date)}</td>
                  <td>{debtors.find((d) => d.debtor_id === b.debtor_id)?.name || b.debtor_id}</td>
                  <td>{b.bill_no}</td>
                  <td className="num">{fmtINR(b.amount)}</td>
                  <td className="num">{fmtINR(b.tds || 0)}</td>
                  <td className="num">{fmtINR(b.settled_amount || 0)}</td>
                  <td className={"num font-bold " + (b.outstanding_amount > 0 ? "text-red-700" : "text-emerald-700")}>{fmtINR(b.outstanding_amount)}</td>
                  <td className="text-xs font-bold">{b.computed_status}</td>
                </tr>
              ))}
              {bills.length === 0 && <tr><td colSpan={8} className="text-slate-500 italic p-3 text-center">No bills yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <DebtorKYC debtors={debtors} onSaved={load} />
    </div>
  );
}

function DebtorKYC({ debtors, onSaved }) {
  const [sel, setSel] = useState("");
  const debtor = useMemo(() => debtors.find((d) => d.debtor_id === sel), [debtors, sel]);
  const [form, setForm] = useState({});
  useEffect(() => { if (debtor) setForm({ ...debtor, vehicle_nos: debtor.vehicle_nos || [] }); }, [debtor]);

  const save = async () => {
    if (!debtor) return;
    try {
      const patch = { ...form };
      delete patch.debtor_id; delete patch.status; delete patch.requested_by; delete patch.requested_at; delete patch.approved_by; delete patch.approved_at; delete patch.created_at;
      await api.patch(`/masters/debtors/${debtor.debtor_id}`, patch);
      toast.success("Debtor KYC saved.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Debtor KYC · contact / bank / GST / vehicles</div>
      <div className="flex gap-2 items-center mb-2 flex-wrap">
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm" data-testid="kyc-debtor-select">
          <option value="">— Pick a debtor —</option>
          {debtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
        </select>
      </div>
      {debtor && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="kyc-form">
          <Input placeholder="Contact person" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          <Input placeholder="Contact phone" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          <Input placeholder="Address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="md:col-span-3" />
          <Input placeholder="Bank name" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
          <Input placeholder="Bank a/c no" value={form.bank_account_no || ""} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} />
          <Input placeholder="IFSC" value={form.ifsc || ""} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} />
          <Input placeholder="GST no" value={form.gst_no || ""} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} />
          <Input placeholder="PAN no" value={form.pan_no || ""} onChange={(e) => setForm({ ...form, pan_no: e.target.value })} />
          <Input type="number" step="0.01" placeholder="Credit limit ₹" value={form.credit_limit || 0} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} />
          <Input type="number" placeholder="Agreed days to pay" value={form.credit_period_days || 30} onChange={(e) => setForm({ ...form, credit_period_days: Number(e.target.value) })} />
          <Input type="number" step="0.01" placeholder="Agreed interest % p.a. (blank = use setting)" value={form.agreed_interest_pct ?? ""} onChange={(e) => setForm({ ...form, agreed_interest_pct: e.target.value === "" ? null : Number(e.target.value) })} />
          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Vehicle numbers</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {(form.vehicle_nos || []).map((v, i) => (
                <span key={v + i} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 rounded-sm px-2 py-1 text-xs">
                  {v} <button onClick={() => setForm({ ...form, vehicle_nos: form.vehicle_nos.filter((_, j) => j !== i) })}><X size={10} /></button>
                </span>
              ))}
              <VehicleAdd onAdd={(v) => setForm({ ...form, vehicle_nos: [...(form.vehicle_nos || []), v] })} />
            </div>
          </div>
          <Button onClick={save} className="bg-slate-900 text-white col-span-full h-10" data-testid="kyc-save">Save KYC</Button>
        </div>
      )}
    </Card>
  );
}

function VehicleAdd({ onAdd }) {
  const [v, setV] = useState("");
  return (
    <span className="inline-flex gap-1 items-center">
      <input value={v} onChange={(e) => setV(e.target.value.toUpperCase())} placeholder="MH-04 XXXX" className="h-7 border border-slate-300 rounded-sm px-2 text-xs w-32" data-testid="kyc-veh-input" />
      <Button size="sm" variant="outline" onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(""); } }} className="h-7 text-xs" data-testid="kyc-veh-add">Add</Button>
    </span>
  );
}

// ---- Receipts ---------------------------------------------------------------
function ReceiptsTab() {
  const [debtors, setDebtors] = useState([]);
  const [bills, setBills] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [sel, setSel] = useState("");
  const [form, setForm] = useState({ debtor_id: "", date_received: todayISO(), amount: 0, mode: "NEFT", reference_no: "", tds: 0, notes: "", bills_settled: [] });

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/masters/debtors"),
        api.get("/phase3/debtor_receipts", { params: { debtor_id: sel || undefined } }),
      ]);
      setDebtors(d.data);
      setReceipts(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillsFor = async (debtor_id) => {
    if (!debtor_id) return;
    try { const r = await api.get("/phase3/debtor_bills", { params: { debtor_id, status: "OPEN" } }); setBills(r.data.filter((b) => b.outstanding_amount > 0.01)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!form.debtor_id || !form.amount) { toast.error("Debtor + amount required."); return; }
    try { await api.post("/phase3/debtor_receipts", form); toast.success("Receipt recorded."); setForm({ ...form, amount: 0, reference_no: "", tds: 0, notes: "", bills_settled: [] }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm bg-slate-50">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Record a payment received</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={form.debtor_id} onChange={(e) => { setForm({ ...form, debtor_id: e.target.value, bills_settled: [] }); loadBillsFor(e.target.value); }} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm" data-testid="new-receipt-debtor">
            <option value="">— Debtor —</option>
            {debtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
          </select>
          <Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} data-testid="new-receipt-date" />
          <Input type="number" step="0.01" placeholder="Amount ₹" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="new-receipt-amount" />
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm" data-testid="new-receipt-mode">
            {["Cheque", "NEFT", "RTGS", "Cash", "UPI"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Input placeholder="Reference no (cheque/UTR)" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} data-testid="new-receipt-ref" />
          <Input type="number" step="0.01" placeholder="TDS ₹ (optional)" value={form.tds || ""} onChange={(e) => setForm({ ...form, tds: Number(e.target.value) })} data-testid="new-receipt-tds" />
        </div>
        {bills.length > 0 && (
          <div className="mt-2">
            <div className="text-xs text-slate-600 mb-1">Apply against these open bills (leave 0 to skip):</div>
            {bills.map((b) => {
              const link = form.bills_settled.find((l) => l.bill_id === b.bill_id);
              return (
                <div key={b.bill_id} className="flex items-center gap-2 text-xs mb-0.5">
                  <span className="flex-1">{fmtDateDMY(b.bill_date)} · <b>{b.bill_no}</b> · Outstanding ₹{fmtINR(b.outstanding_amount)}</span>
                  <Input type="number" step="0.01" value={link?.amount || 0}
                         onChange={(e) => {
                           const amt = Number(e.target.value);
                           const other = form.bills_settled.filter((l) => l.bill_id !== b.bill_id);
                           setForm({ ...form, bills_settled: amt > 0 ? [...other, { bill_id: b.bill_id, amount: amt }] : other });
                         }}
                         className="w-32 h-8" data-testid={`receipt-bill-alloc-${b.bill_id}`} />
                </div>
              );
            })}
          </div>
        )}
        <Button onClick={save} className="mt-2 bg-slate-900 text-white h-10" data-testid="new-receipt-save">Save receipt</Button>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Payments received</div>
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-2 bg-white text-sm">
            <option value="">All debtors</option>
            {debtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm" data-testid="receipts-table">
            <thead><tr><th>Date</th><th>Debtor</th><th>Mode</th><th>Ref #</th><th>Amount ₹</th><th>TDS ₹</th><th>Bills settled</th></tr></thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.receipt_id} data-testid={`receipt-row-${r.receipt_id}`}>
                  <td>{fmtDateDMY(r.date_received)}</td>
                  <td>{debtors.find((d) => d.debtor_id === r.debtor_id)?.name || r.debtor_id}</td>
                  <td>{r.mode}</td>
                  <td className="font-mono text-xs">{r.reference_no}</td>
                  <td className="num font-bold">{fmtINR(r.amount)}</td>
                  <td className="num">{fmtINR(r.tds || 0)}</td>
                  <td className="text-xs">{(r.bills_settled || []).map((l) => `${l.bill_id.slice(0, 6)} ₹${l.amount}`).join(", ") || "—"}</td>
                </tr>
              ))}
              {receipts.length === 0 && <tr><td colSpan={7} className="text-slate-500 italic p-3 text-center">No receipts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---- Interest & margin ------------------------------------------------------
function InterestTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/phase3/debtor_interest").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  const totalInterest = rows.reduce((s, r) => s + r.interest_cost_rs, 0);
  const totalOutstanding = rows.reduce((s, r) => s + r.outstanding_rs, 0);
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <div className="text-xs text-slate-600 mb-2">
          Interest cost per debtor = outstanding ₹ × (agreed% or cost-of-funds%) ÷ 100 × overdue days ÷ 365. AGREED vs ACTUAL shows the gap between what they promised and what they do.
        </div>
        <div className="flex gap-6 flex-wrap text-sm">
          <div><span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Total outstanding</span><div className="num text-2xl font-bold">{fmtINR(totalOutstanding)}</div></div>
          <div><span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Total interest cost</span><div className="num text-2xl font-bold text-red-700">{fmtINR(totalInterest)}</div></div>
        </div>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm overflow-x-auto">
        <table className="dense-table w-full text-sm" data-testid="interest-table">
          <thead><tr><th>Debtor</th><th>Agreed (d)</th><th>Actual avg (d)</th><th>Bills</th><th>Breaches</th><th>Outstanding ₹</th><th>Interest cost ₹</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.debtor_id} className={r.interest_cost_rs > 0 ? "state-warn" : ""} data-testid={`interest-row-${r.debtor_id}`}>
                <td>{r.name}</td>
                <td className="num">{r.agreed_days_to_pay}</td>
                <td className={"num font-bold " + (r.avg_actual_days != null && r.avg_actual_days > r.agreed_days_to_pay ? "text-red-700" : "text-emerald-700")}>
                  {r.avg_actual_days == null ? "—" : r.avg_actual_days}
                </td>
                <td className="num">{r.bills_count}</td>
                <td className={"num font-bold " + (r.breaches > 0 ? "text-red-700" : "text-emerald-700")}>{r.breaches}</td>
                <td className="num">{fmtINR(r.outstanding_rs)}</td>
                <td className="num font-bold text-red-700">{fmtINR(r.interest_cost_rs)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-slate-500 italic p-3 text-center">No debtor data yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
